import type { AssistantError } from '@agently/types';
import { isAxiosError } from 'axios';

export type { AssistantError };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export function isAbortError(error: unknown) {
  return isRecord(error) && error.name === 'AbortError';
}

/** Builds an `AssistantError` from a response status and its (JSON) body. */
export function assistantErrorFromBody(
  status: number,
  body: unknown,
): AssistantError {
  if (isRecord(body) && typeof body.code === 'string') {
    return {
      code: body.code,
      message: typeof body.message === 'string' ? body.message : body.code,
      status,
    };
  }

  return { code: 'UNKNOWN', message: 'Unexpected response', status };
}

export function parseAssistantError(error: unknown): AssistantError {
  if (isAxiosError(error)) {
    return error.response
      ? assistantErrorFromBody(error.response.status, error.response.data)
      : { code: 'NETWORK', message: error.message };
  }
  if (error instanceof TypeError) {
    return { code: 'NETWORK', message: error.message };
  }
  if (isRecord(error) && typeof error.code === 'string') {
    return {
      code: error.code,
      message: typeof error.message === 'string' ? error.message : error.code,
      ...(typeof error.status === 'number' ? { status: error.status } : {}),
      ...(error.phase === 'pre-stream' || error.phase === 'stream'
        ? { phase: error.phase }
        : {}),
    };
  }

  return { code: 'UNKNOWN', message: 'Unknown error' };
}
